import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { usePageState } from "@/hooks/usePageState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { pageStateQueryOptions, PageAccessError } from "@/lib/queries";
import { PageError } from "@/features/auth/components/PageError";

/**
 * Pre Flight – Scripts layout route: gated by the grantable SCRIPT_REVIEW_VIEW_PAGE
 * permission for the active tenant (same pattern as pre-flight / personas).
 */
export const Route = createFileRoute("/_authenticated/pre-flight-scripts")({
  beforeLoad: () => assertPagePermission("pre-flight-scripts"),
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData(pageStateQueryOptions("pre-flight-scripts"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      }),
  component: ScriptReviewLayout,
  errorComponent: ({ error }) => (
    <PageError is403={error instanceof PageAccessError} />
  ),
});

function ScriptReviewLayout() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.preFlightScripts", "Pre Flight – Scripts"));
  const { isLoading, error } = usePageState("pre-flight-scripts");

  if (isLoading) {
    return <div className="flex h-full items-center justify-center">Loading…</div>;
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <Outlet />;
}
